import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import WhatsNewDialog from "@/components/WhatsNewDialog";
import {
  Plus,
  Briefcase,
  Users,
  CheckCircle2,
  ArrowUpRight,
  Sparkles,
  BellRing,
  TrendingUp,
  Clock,
} from "lucide-react";
import { motion } from "framer-motion";
import CandidatesPanel from "@/components/CandidatesPanel";
import BillingSummaryWidget from "@/components/BillingSummaryWidget";
import CareersAnalyticsWidget from "@/components/CareersAnalyticsWidget";
import EmailEngagementWidget from "@/components/EmailEngagementWidget";
import FunnelWidget from "@/components/FunnelWidget";
import BackgroundCheckPipelineWidget from "@/components/BackgroundCheckPipelineWidget";
import UnfinalizedRecordingsWidget from "@/components/UnfinalizedRecordingsWidget";
import HiringManagerResponses from "@/components/HiringManagerResponses";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reminderStats, setReminderStats] = useState(null);
  const [panel, setPanel] = useState({ open: false, status: null });
  const rolesRef = useRef(null);

  const scrollToRoles = () => {
    rolesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    api
      .get("/jobs")
      .then(({ data }) => setJobs(data))
      .finally(() => setLoading(false));
    api
      .get("/dashboard/reminder-stats")
      .then(({ data }) => setReminderStats(data))
      .catch(() => {
        /* non-blocking */
      });
  }, []);

  const totalCandidates = jobs.reduce((s, j) => s + (j.candidate_count || 0), 0);
  const totalCompleted = jobs.reduce((s, j) => s + (j.completed_count || 0), 0);

  return (
    <main data-testid="dashboard-page" className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-12">
      <WhatsNewDialog />
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
            Hi {user?.name?.split(" ")[0]} —
          </div>
          <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            Your Hiring Dashboard
          </h1>
        </div>
        <Button
          data-testid="dashboard-new-job-btn"
          onClick={() => navigate("/jobs/new")}
          className="bg-brand-gradient rounded-sm px-5 py-5 text-white"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add New Job Title
        </Button>
      </div>

      {/* Trial banner */}
      {user?.is_trial && user?.subscription_active_until && (() => {
        const ms = new Date(user.subscription_active_until).getTime() - Date.now();
        const days = Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
        return (
          <div
            data-testid="trial-banner"
            className="mt-6 flex flex-wrap items-center justify-between gap-3 border border-blue-200 bg-blue-50 px-5 py-3"
          >
            <div className="flex items-center gap-2 text-sm text-slate-900">
              <Sparkles className="h-4 w-4 text-blue-700" />
              <span>
                <strong>{days} {days === 1 ? "day" : "days"}</strong> left on your free Pro trial — every feature is unlocked.
              </span>
            </div>
            <Button
              asChild
              size="sm"
              className="rounded-sm border border-blue-300 bg-blue-100 text-blue-700 hover:bg-blue-200 hover:text-blue-800"
              data-testid="trial-upgrade-btn"
            >
              <Link to="/billing">Upgrade now</Link>
            </Button>
          </div>
        );
      })()}

      {/* KPI strip */}
      <div className="mt-10 grid gap-px bg-slate-200 sm:grid-cols-3">
        {[
          {
            label: "Open Jobs",
            value: jobs.length,
            icon: Briefcase,
            bg: "bg-emerald-600",
            text: "text-white",
            sub: "text-emerald-100",
            iconColor: "text-emerald-200",
            onClick: scrollToRoles,
          },
          {
            label: "Candidates",
            value: totalCandidates,
            icon: Users,
            bg: "bg-blue-700",
            text: "text-white",
            sub: "text-blue-100",
            iconColor: "text-blue-200",
            onClick: () => setPanel({ open: true, status: null }),
          },
          {
            label: "Completed",
            value: totalCompleted,
            icon: CheckCircle2,
            bg: "bg-slate-900",
            text: "text-white",
            sub: "text-slate-300",
            iconColor: "text-slate-400",
            onClick: () => setPanel({ open: true, status: "completed" }),
          },
        ].map((k) => (
          <button
            key={k.label}
            type="button"
            onClick={k.onClick}
            data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`${k.bg} ${k.text} group p-6 text-left transition-transform hover:scale-[1.01] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40`}
          >
            <div className="flex items-center justify-between">
              <div
                className={`text-xs font-bold uppercase tracking-[0.2em] ${k.sub}`}
              >
                {k.label}
              </div>
              <k.icon className={`h-4 w-4 ${k.iconColor}`} />
            </div>
            <div className="kpi-stat mt-3 font-heading text-4xl font-semibold">
              {k.value}
            </div>
            <div
              className={`mt-2 flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] ${k.sub} opacity-0 transition-opacity group-hover:opacity-100`}
            >
              View
              <ArrowUpRight className="h-3 w-3" />
            </div>
          </button>
        ))}
      </div>

      {/* Billing summary (plan + seats) */}
      <BillingSummaryWidget />

      {/* Careers embed analytics */}
      <CareersAnalyticsWidget />

      {/* Email engagement (Resend webhook-driven) */}
      <EmailEngagementWidget />

      {/* Pipeline funnel analytics */}
      <FunnelWidget />

      {/* Background check pipeline (only renders when Checkr is configured) */}
      <BackgroundCheckPipelineWidget />

      {/* Crash-recovery: surface any live-call recordings stuck mid-upload */}
      <UnfinalizedRecordingsWidget />

      {/* Hiring-manager responses inbox (briefs sent → 👍/🤔/👎 verdicts) */}
      <div className="mt-6">
        <HiringManagerResponses />
      </div>

      {/* Reminder analytics */}
      {reminderStats &&
        (reminderStats.total_reminders_sent > 0 ||
          reminderStats.pending_followups > 0) && (
          <div
            data-testid="reminder-analytics"
            className="mt-6 border border-slate-200 bg-white"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-5 py-3">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                <BellRing className="h-3 w-3" />
                Reminder activity
              </div>
              <Link
                to="/settings"
                className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700 hover:underline"
              >
                Cadence · every {reminderStats.cadence_hours}h · max{" "}
                {reminderStats.max_reminders}
              </Link>
            </div>
            <div className="grid gap-px bg-slate-100 sm:grid-cols-4">
              <div className="bg-white p-4">
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  <BellRing className="h-3 w-3" /> Sent · all-time
                </div>
                <div
                  data-testid="reminder-stat-total"
                  className="kpi-stat mt-2 font-heading text-2xl font-semibold"
                >
                  {reminderStats.total_reminders_sent}
                </div>
              </div>
              <div className="bg-white p-4">
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  <Clock className="h-3 w-3" /> Sent · 7d
                </div>
                <div
                  data-testid="reminder-stat-7d"
                  className="kpi-stat mt-2 font-heading text-2xl font-semibold"
                >
                  {reminderStats.reminders_sent_7d}
                </div>
              </div>
              <div className="bg-white p-4">
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700">
                  <TrendingUp className="h-3 w-3" /> Resumed after nudge
                </div>
                <div
                  data-testid="reminder-stat-engaged"
                  className="kpi-stat mt-2 font-heading text-2xl font-semibold"
                >
                  {reminderStats.nudged_then_engaged}
                </div>
              </div>
              <div className="bg-white p-4">
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700">
                  <Users className="h-3 w-3" /> Awaiting follow-up
                </div>
                <div
                  data-testid="reminder-stat-pending"
                  className="kpi-stat mt-2 font-heading text-2xl font-semibold"
                >
                  {reminderStats.pending_followups}
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Job list */}
      <div ref={rolesRef} className="mt-12 scroll-mt-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-xl font-medium">Roles</h2>
          {user?.subscription_plan ? (
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">
              {user.subscription_plan} plan active
            </div>
          ) : (
            <Link
              to="/billing"
              className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700 hover:underline"
            >
              Upgrade →
            </Link>
          )}
        </div>

        {loading ? (
          <div className="border border-slate-200 p-12 text-center text-sm text-slate-500">
            Loading…
          </div>
        ) : jobs.length === 0 ? (
          <div
            data-testid="dashboard-empty"
            className="grid grid-cols-1 items-center gap-8 border border-slate-200 p-12 lg:grid-cols-2"
          >
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
                <Sparkles className="mr-1 inline h-3 w-3" /> Get started
              </div>
              <h3 className="mt-2 font-heading text-2xl font-medium">
                Create your first role
              </h3>
              <p className="mt-3 text-sm text-slate-500">
                Drop a job title and we'll generate the interview, all you need
                to do is share the link.
              </p>
              <Button
                data-testid="empty-create-job-btn"
                onClick={() => navigate("/jobs/new")}
                className="mt-6 rounded-sm bg-blue-700 hover:bg-blue-600"
              >
                Create role
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-3 text-sm text-slate-500">
              <div className="flex gap-3"><span className="font-mono text-blue-700">01</span> Drop a role title.</div>
              <div className="flex gap-3"><span className="font-mono text-blue-700">02</span> AI writes 5 questions.</div>
              <div className="flex gap-3"><span className="font-mono text-blue-700">03</span> Share the link with candidates.</div>
              <div className="flex gap-3"><span className="font-mono text-blue-700">04</span> Review scored, transcribed answers.</div>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {jobs.map((j, idx) => (
              <motion.button
                key={j.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: idx * 0.04 }}
                onClick={() => navigate(`/jobs/${j.id}`)}
                data-testid={`job-card-${j.id}`}
                className="group flex flex-col items-start gap-4 border border-slate-900 bg-white p-6 text-left transition-colors duration-200 hover:bg-slate-50"
              >
                <div className="flex w-full items-start justify-between">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
                      {new Date(j.created_at).toLocaleDateString()}
                    </div>
                    <h3 className="mt-2 font-heading text-lg font-medium leading-tight">
                      {j.title}
                    </h3>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-slate-400 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-blue-700" />
                </div>
                <p className="line-clamp-2 text-sm text-slate-500">
                  {j.description || "—"}
                </p>
                <div className="mt-auto flex w-full items-center justify-between border-t border-slate-200 pt-4 text-sm">
                  <div className="flex items-center gap-1 text-red-600">
                    <Users className="h-3 w-3" />
                    {j.candidate_count} candidate{j.candidate_count === 1 ? "" : "s"}
                  </div>
                  <div className="font-medium text-blue-700">
                    {j.completed_count} completed
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <CandidatesPanel
        open={panel.open}
        status={panel.status}
        onOpenChange={(v) => setPanel({ open: v, status: panel.status })}
      />
    </main>
  );
}
